import React, { useId, useState } from "react";
import {
  Alert,
  Box,
  ButtonBase,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TONE_COLOR } from "./DecisionRail";
import { TOUCH, focusRing } from "./regmStyles";
import HelpTip from "./HelpTip";
import HELP from "./helpText";
import {
  capRows,
  evidenceSections,
  formatWindow,
  groupPositions,
  isUnresolved,
  matchModeSummary,
  wildcardSummary,
} from "./regmFormat";

const MAX_TABLE_ROWS = 50;

// Wide tables scroll inside their own box so the page never scrolls sideways.
const TableScroll = ({ children }) => <Box sx={{ overflowX: "auto" }}>{children}</Box>;

const HiddenNotice = ({ text }) => (
  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
    {text}
  </Typography>
);

const TradesTable = ({ rows }) => {
  const { shown, hidden } = capRows(rows, MAX_TABLE_ROWS);
  return (
    <>
      <TableScroll>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Symbol</TableCell>
              <TableCell>ISIN</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Filled</TableCell>
              <TableCell align="right">Avg price</TableCell>
              <TableCell>Strategy</TableCell>
              <TableCell>Broker</TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((t, i) => (
              <TableRow key={i}>
                <TableCell>{t.trade_date}</TableCell>
                <TableCell>{t.symbol}</TableCell>
                <TableCell>{t.isin}</TableCell>
                <TableCell>{t.pos_action}</TableCell>
                <TableCell>{t.sec_type}</TableCell>
                <TableCell align="right">{t.filled_amt}</TableCell>
                <TableCell align="right">{t.trade_avg_price}</TableCell>
                <TableCell>{t.strategy1 || t.strategy_type}</TableCell>
                <TableCell>{t.broker_code}</TableCell>
                <TableCell>{t.source}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableScroll>
      {hidden > 0 && <HiddenNotice text={`${hidden} more rows will be stored in the record`} />}
    </>
  );
};

const netCell = { fontWeight: 700 };

// Positions grouped per symbol; each group ends with a bold net row taken verbatim from the server.
const PositionsTable = ({ snapshot }) => {
  const { groups, netAvailable, hiddenRows, hiddenNets } = groupPositions(snapshot, MAX_TABLE_ROWS);
  return (
    <>
      {!netAvailable && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Per-symbol net positions unavailable for this result.
        </Typography>
      )}
      <TableScroll>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Symbol</TableCell>
              <TableCell>Strategy</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Realtime quantity</TableCell>
              <TableCell align="right">Net trading quantity</TableCell>
              <TableCell>In net position</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((g) => (
              <React.Fragment key={g.key}>
                {g.rows.map((p, i) => (
                  <TableRow key={`${g.key}-${i}`}>
                    <TableCell>{p.symbol}</TableCell>
                    <TableCell>{p.strategy}</TableCell>
                    <TableCell>{p.securityType}</TableCell>
                    <TableCell align="right">{p.realtimeQuantity}</TableCell>
                    <TableCell align="right">{p.netTradingQuantity}</TableCell>
                    <TableCell>{p.inNet ? "Yes" : "No (swap)"}</TableCell>
                  </TableRow>
                ))}
                {g.net && (
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell sx={netCell}>{g.net.symbol} net</TableCell>
                    <TableCell />
                    <TableCell>{g.net.securityType}</TableCell>
                    <TableCell align="right" sx={netCell}>
                      {g.net.netQuantity}
                    </TableCell>
                    <TableCell />
                    <TableCell>{g.net.inNet ? "Yes" : "No (swap)"}</TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableScroll>
      {(hiddenRows > 0 || hiddenNets > 0) && (
        <HiddenNotice
          text={`${hiddenRows} more position rows${
            hiddenNets > 0 ? ` and ${hiddenNets} more net rows` : ""
          } will be stored in the record`}
        />
      )}
    </>
  );
};

// One evidence section. Empty sections are a single line; others toggle open and closed, and the
// summary (count and verdict) stays visible when closed.
// tips are info buttons placed beside the toggle (siblings, never inside the toggle button).
const Section = ({ title, info, tips = [], children }) => {
  const [open, setOpen] = useState(info.defaultExpanded);
  const bodyId = useId();
  const heading = (
    <>
      <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600, mr: 1 }}>
        {title}
        {info.empty ? "" : ` (${info.count})`}
      </Typography>
      <Typography component="span" variant="body2" sx={{ color: TONE_COLOR[info.tone] }}>
        {info.summary}
      </Typography>
    </>
  );
  const tipButtons = tips.map((t) => <HelpTip key={t.label} text={t.text} label={t.label} />);
  return (
    <Box component="section" sx={{ borderTop: 1, borderColor: "divider", py: 1 }}>
      {info.empty ? (
        <Box
          component="h3"
          sx={{ m: 0, minHeight: TOUCH, display: "flex", alignItems: "center", flexWrap: "wrap", pl: 4 }}
        >
          {heading}
          {tipButtons}
        </Box>
      ) : (
        <>
          <Box component="h3" sx={{ m: 0, display: "flex", alignItems: "center" }}>
            <ButtonBase
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls={bodyId}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: TOUCH,
                justifyContent: "flex-start",
                textAlign: "left",
                flexWrap: "wrap",
                borderRadius: 1,
                ...focusRing,
              }}
            >
              <ExpandMoreIcon
                fontSize="small"
                sx={{ mr: 1.5, transform: open ? "none" : "rotate(-90deg)", color: "text.secondary" }}
              />
              {heading}
            </ButtonBase>
            {tipButtons}
          </Box>
          <Box id={bodyId} hidden={!open} sx={{ pt: 1 }}>
            {open && children}
          </Box>
        </>
      )}
    </Box>
  );
};

// Scope (what was searched and the window), then trades, positions and related instruments.
const EvidenceSections = ({ result, children }) => {
  const snapshot = result.snapshot || {};
  const sections = evidenceSections(result);
  const resolved = Array.isArray(snapshot.resolved_symbols) ? snapshot.resolved_symbols : [];
  const scopeLines = [matchModeSummary(result), wildcardSummary(result)].filter(Boolean);
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
          {result.security}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            Window {formatWindow(result.window_start, result.window_end)}
            {scopeLines.length > 0 ? `. ${scopeLines.join(". ")}` : ""}
          </Typography>
          <HelpTip text={HELP.window} label="window" spaced />
          <HelpTip text={HELP.matchMode} label="match mode" spaced />
        </Box>
        {resolved.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              Resolved symbols: {resolved.join(", ")}
            </Typography>
            <HelpTip text={HELP.resolvedSymbols} label="resolved symbols" />
          </Box>
        )}
        {(result.window_notes || []).map((note) => (
          <Typography key={note} variant="body2" color="text.secondary">
            {note}
          </Typography>
        ))}
        {snapshot.positions_as_of && (
          <Typography variant="body2" color="text.secondary">
            Positions as of {snapshot.positions_as_of}
          </Typography>
        )}
        {(snapshot.future_sessions || []).length > 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Sessions not yet traded (skipped): {snapshot.future_sessions.join(", ")}
          </Alert>
        )}
        {isUnresolved(result) && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            This security could not be matched to any trade or position in the dashboard data. No sales
            or short sales could be checked because nothing matched; confirm the identifier.
          </Alert>
        )}
      </Box>
      {children}
      <Section
        title="Short and sale trades"
        info={sections.trades}
        tips={[
          { label: "short sales", text: HELP.shortSales },
          { label: "sales of long positions", text: HELP.longSales },
        ]}
      >
        <TradesTable rows={snapshot.trades || []} />
      </Section>
      <Section
        title="Positions"
        info={sections.positions}
        tips={[
          { label: "positions", text: HELP.positions },
          { label: "net position", text: HELP.netPosition },
        ]}
      >
        <PositionsTable snapshot={snapshot} />
      </Section>
      <Section
        title="Related instruments"
        info={sections.related}
        tips={[{ label: "related instruments", text: HELP.relatedInstruments }]}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Options, convertibles and swaps; informational.
        </Typography>
        <TradesTable rows={snapshot.related || []} />
      </Section>
    </Stack>
  );
};

export default EvidenceSections;
